/**
 * Connected `lake_search_folders_by_name` tool card: parses the backend
 * payload and hands pure data to LakeSearchFoldersByNameView. Clicking a folder
 * routes to ``/data?label=<lbl>&path=<path>`` which the lake page reads to
 * scope its folder tree.
 */

import { makeAssistantToolUI } from '@assistant-ui/react'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { LakeSearchFoldersByNameView } from '@/components/agent/tools/LakeSearchFoldersByNameView'
import {
  type LakeSearchFoldersByNameArgs,
  type LakeSearchFoldersByNameResult,
  parseResult,
} from './lake-search-folders-by-name'

type Status = { type: 'running' | 'complete' | 'incomplete' | string }

interface LakeSearchFoldersByNameRenderProps {
  args: LakeSearchFoldersByNameArgs
  result?: LakeSearchFoldersByNameResult | string
  status: Status
}

function LakeSearchFoldersByNameRender({
  args,
  result,
  status,
}: LakeSearchFoldersByNameRenderProps) {
  const navigate = useNavigate()

  const running = status.type === 'running'
  const parsed = useMemo(() => parseResult(result), [result])
  const errored = Boolean(parsed?.error) || status.type === 'incomplete'

  const onOpenFolder = useCallback(
    (split: { label: string; path: string }) => {
      const params = new URLSearchParams()
      params.set('label', split.label)
      if (split.path) params.set('path', split.path)
      navigate(`/data?${params.toString()}`)
    },
    [navigate]
  )

  return (
    <LakeSearchFoldersByNameView
      args={args}
      parsed={parsed}
      running={running}
      errored={errored}
      onOpenFolder={onOpenFolder}
    />
  )
}

export const LakeSearchFoldersByNameToolUI = makeAssistantToolUI<
  LakeSearchFoldersByNameArgs,
  LakeSearchFoldersByNameResult | string
>({
  toolName: 'lake_search_folders_by_name',
  render: LakeSearchFoldersByNameRender,
})
